import type { GetServerSideProps, GetServerSidePropsContext } from 'next';
import { useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';
import { Auth, useAuth } from '../AuthProvider';
import { createPrefetchClient } from '@zg-rentals/next-react-query5';
import { forwardCookies } from '@zg-rentals/next/server';
import { getPageFrameData, PageFrame, PageFrameComponent } from '@zillow/page-frame-next/shopper-platform';
import { useQuery } from '@zg-rentals/react-query5';
import { client } from '../rentalClient';
import { Documents, EmptyDocuments, EmptyRent, Rent, YourRentalPageLayout } from '../components/Layout';
import { EmptyHeader, Header } from '../components/Header';
import { AddRentalModal, ContactIreneModal, ViewRentalsModal } from '../components/Modals';
import { getRentals } from '../queries/getRentals';
import { useYourRentalPage } from '../hooks';
import { emitYourRentalPageView } from '../analytics/events';
import { DocumentsSkeleton, HeaderSkeleton, RentSkeleton } from '../components/Skeletons';
import { getRinsUpsell } from '../queries/getRinsUpsell';
import { getParsedZguid, hasEmptyRental } from '../utils';
import { useIsInMobileWebView } from '../hooks';
import { YourRentalPageProvider } from '../contexts/YourRentalPageContext';
import { zillowLoginUrl } from '@zg-rentals/login-zillow/server';

export const getServerSideProps = (async (context: GetServerSidePropsContext) => {
  const httpClient = forwardCookies(context, client);
  const ssrClient = createPrefetchClient();

  const user = await ssrClient.prefetchQuery(Auth.queryOptions({ httpClient }));
  const isAuthorized = Auth.isAuthorized(user, {});

  // Rewards enroll link (unauthenticated) or soft session without strong auth — force login, return with ap=x
  if (
    !user.isLoggedIn &&
    context.query.ap !== 'x' &&
    (context.query.enrollRewards === 'true' || Boolean(user.userToken))
  ) {
    const separator = context.resolvedUrl.includes('?') ? '&' : '?';
    return {
      redirect: {
        destination: zillowLoginUrl({
          baseURL: process.env.AUTH_BASE_URL,
          entryPoint: 'your-rental-web',
          redirectUri: `/rental/your-rental${context.resolvedUrl}${separator}ap=x`,
          forceLogin: true,
        }),
      },
      props: {},
    };
  }

  // Preload data for fully strong-authorized users
  if (isAuthorized) {
    await ssrClient.prefetchQuery(getRentals(httpClient));
  }

  const pageFrameData = await getPageFrameData(context, {
    pfsHost: process.env.NEXT_PUBLIC_PAGE_FRAME_SERVICE,
    regionalizedLinks: false,
    useVisualRefreshTheme: true,
  });

  const ssrProps = ssrClient.getServerSideProps();

  // Sanitize pageFrameData: Next.js cannot serialize undefined values in getServerSideProps
  // Convert undefined to null using JSON parse/stringify
  const sanitizedPageFrameData = JSON.parse(JSON.stringify(pageFrameData));

  return {
    props: {
      ...ssrProps.props,
      pageFrameData: sanitizedPageFrameData,
    },
  };
}) satisfies GetServerSideProps;

function YourRentalPageLayoutContainer({ pageFrameData }: { pageFrameData: object }) {
  const router = useRouter();
  const { user } = useAuth();
  const isInMobileWebView = useIsInMobileWebView();
  const isLoggedIn = user?.isLoggedIn ?? false;

  const autoOpenRewardsEnroll = router.query.enrollRewards === 'true';

  useEffect(() => {
    if (autoOpenRewardsEnroll) {
      const { enrollRewards: _enrollRewards, ap: _ap, ...restQuery } = router.query;
      router.replace({ pathname: router.pathname, query: restQuery }, undefined, { shallow: true });
    }
  }, [autoOpenRewardsEnroll, router]);
  const { data: rentalsData, error: rentalsError } = useQuery({
    ...getRentals(client),
    enabled: isLoggedIn,
  });

  const parsedGuid = getParsedZguid();
  const { data: rinsUpsellData } = useQuery({
    ...getRinsUpsell(parsedGuid, client),
    enabled: !isLoggedIn && !!parsedGuid && parsedGuid.trim() !== '',
  });
  const isError = isLoggedIn && rentalsError !== null;
  const renterProfile = rentalsData?.yourRentalPageData.renterProfile;

  const {
    selectedProperty,
    allProperties,
    defaultProperty,
    showBreadcrumb,
    selectedTab,
    setSelectedTab,
    handlePropertyChange,
    handleAddRental,
    handleBreadcrumbClick,
    isLoadingRinsUpsellData,
  } = useYourRentalPage({
    rentalsData,
    encodedZuid: user?.encodedZuid || '',
  });

  const showSkeleton =
    (isLoggedIn && allProperties.length > 0 && selectedProperty === undefined) || isLoadingRinsUpsellData;
  const rentersInsuranceCardData = isLoggedIn
    ? selectedProperty?.rinsSectionDto?.rinsCard
    : rinsUpsellData?.renterInsuranceCard;

  useEffect(() => {
    emitYourRentalPageView(renterProfile);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const contactIreneModal = useMemo(
    () =>
      selectedProperty?.contactSection ? (
        <ContactIreneModal contactSection={selectedProperty.contactSection} />
      ) : undefined,
    [selectedProperty?.contactSection],
  );

  const viewRentalsModal = useMemo(
    () =>
      allProperties.length > 1 && selectedProperty ? (
        <ViewRentalsModal
          yourRentalProperties={allProperties}
          selectedProperty={selectedProperty}
          onPropertyChange={handlePropertyChange}
        />
      ) : undefined,
    [allProperties, selectedProperty, handlePropertyChange],
  );

  const addRentalModal = useMemo(
    () => (!hasEmptyRental(isLoggedIn, allProperties) ? <AddRentalModal onAddRental={handleAddRental} /> : undefined),
    [isLoggedIn, allProperties, handleAddRental],
  );

  const rentTabContent = useMemo(
    () => (
      <Rent
        paymentsSection={selectedProperty?.paymentSection}
        creditReportingSection={selectedProperty?.creditReportingSection}
        rewardsSection={selectedProperty?.rewardsSection}
        listingAlias={selectedProperty?.listingSection?.listingAlias}
        autoOpenRewardsEnroll={autoOpenRewardsEnroll}
      />
    ),
    [
      selectedProperty?.paymentSection,
      selectedProperty?.creditReportingSection,
      selectedProperty?.rewardsSection,
      selectedProperty?.listingSection?.listingAlias,
      autoOpenRewardsEnroll,
    ],
  );

  const documentsTabContent = useMemo(
    () => (
      <Documents
        leaseSection={selectedProperty?.leaseSection}
        propertyConditionSection={selectedProperty?.propertyConditionSection}
        rentersInsuranceCard={rentersInsuranceCardData}
        listingSection={selectedProperty?.listingSection}
      />
    ),
    [
      selectedProperty?.leaseSection,
      selectedProperty?.propertyConditionSection,
      rentersInsuranceCardData,
      selectedProperty?.listingSection,
    ],
  );

  const header = useMemo(
    () => (
      <Header
        isLoggedIn={isLoggedIn}
        listingSection={selectedProperty?.listingSection}
        hasMultipleRentals={allProperties.length > 1}
        showBreadcrumb={showBreadcrumb}
        breadcrumbAddress={defaultProperty?.listingSection?.address}
        contactIreneModal={contactIreneModal}
        viewRentalsModal={viewRentalsModal}
        addRentalModal={addRentalModal}
        onBreadcrumbClick={handleBreadcrumbClick}
      />
    ),
    [
      isLoggedIn,
      selectedProperty?.listingSection,
      allProperties.length,
      showBreadcrumb,
      defaultProperty?.listingSection?.address,
      contactIreneModal,
      viewRentalsModal,
      addRentalModal,
      handleBreadcrumbClick,
    ],
  );

  const loggedOutPageContent = useMemo(() => {
    const content = (
      <YourRentalPageProvider renterProfile={renterProfile}>
        <YourRentalPageLayout
          header={<EmptyHeader isLoggedIn={false} hasMultipleRentals={false} />}
          rentTabContent={<EmptyRent />}
          documentsTabContent={<EmptyDocuments />}
        />
      </YourRentalPageProvider>
    );

    if (isInMobileWebView) {
      return content;
    }

    return (
      <PageFrame
        pageFrameData={pageFrameData}
        components={[
          PageFrameComponent.TOPNAV,
          PageFrameComponent.FOOTER,
          PageFrameComponent.BODYSCRIPTS,
          PageFrameComponent.REGLOGIN,
          PageFrameComponent.PRODUCTNAV,
        ]}
      >
        {content}
      </PageFrame>
    );
  }, [isInMobileWebView, pageFrameData, renterProfile]);

  const loggedInPageContent = useMemo(() => {
    const pageContent = (
      <YourRentalPageProvider renterProfile={renterProfile}>
        <YourRentalPageLayout
          header={showSkeleton ? <HeaderSkeleton /> : header}
          rentTabContent={showSkeleton ? <RentSkeleton /> : rentTabContent}
          documentsTabContent={showSkeleton ? <DocumentsSkeleton /> : documentsTabContent}
          selectedTab={selectedTab}
          onTabChange={setSelectedTab}
          isError={isError}
        />
      </YourRentalPageProvider>
    );

    if (isInMobileWebView) {
      return pageContent;
    }

    return (
      <PageFrame
        pageFrameData={pageFrameData}
        components={[
          PageFrameComponent.TOPNAV,
          PageFrameComponent.FOOTER,
          PageFrameComponent.BODYSCRIPTS,
          PageFrameComponent.REGLOGIN,
          PageFrameComponent.PRODUCTNAV,
        ]}
      >
        {pageContent}
      </PageFrame>
    );
  }, [
    renterProfile,
    isInMobileWebView,
    pageFrameData,
    showSkeleton,
    header,
    rentTabContent,
    documentsTabContent,
    selectedTab,
    setSelectedTab,
    isError,
  ]);

  return <Auth.Boundary onUnauthorized={loggedOutPageContent}>{loggedInPageContent}</Auth.Boundary>;
}

export default YourRentalPageLayoutContainer;
